import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { serializeAsJSON } from "@excalidraw/excalidraw";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  DRAWLAB_OPEN_SHARE_EVENT,
  notifyDrawLabShareStatus,
} from "@excalidraw/excalidraw/components/DrawLabToolbarActions";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import "./DrawLabShareDialog.scss";

// 5 MB cap — phải khớp với Worker MAX_SCENE_BYTES.
const MAX_SCENE_BYTES = 5 * 1024 * 1024;

type ShareState =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "done"; url: string; copied: boolean }
  | { kind: "error"; message: string };

const WORKER_URL = import.meta.env.VITE_APP_SHARE_WORKER_URL || "";

const copyTextToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      return document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
  }
};

export const DrawLabShareDialog = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<ShareState>({ kind: "idle" });
  const linkInputRef = useRef<HTMLInputElement>(null);

  // Esc đóng dialog.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const close = () => setIsOpen(false);

  const handleCreateLink = useCallback(
    async (mode: "direct" | "dialog" = "dialog") => {
      const fail = (message: string) => {
        setState({ kind: "error", message });
        setIsOpen(true);
        excalidrawAPI?.setToast?.({ message, duration: 3000 });
      };

      if (!excalidrawAPI) {
        fail(t("drawlab.share.errorEditorNotReady"));
        return;
      }
      if (!WORKER_URL) {
        fail(t("drawlab.share.errorNoBackend"));
        return;
      }

      const serialized = serializeAsJSON(
        excalidrawAPI.getSceneElementsIncludingDeleted(),
        excalidrawAPI.getAppState(),
        excalidrawAPI.getFiles(),
        "local",
      );

      if (serialized.length > MAX_SCENE_BYTES) {
        fail(t("drawlab.share.errorSceneTooLarge"));
        return;
      }

      // Nếu scene có frame → share cũng chạy presentation mode read-only.
      const hasFrames = excalidrawAPI
        .getSceneElementsIncludingDeleted()
        .some((e) => e.type === "frame");

      if (mode === "dialog") {
        setState({ kind: "uploading" });
      }

      try {
        const response = await fetch(`${WORKER_URL}/api/share`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: serialized,
        });

        if (!response.ok) {
          const data = (await response.json().catch(() => null)) as {
            error?: string;
          } | null;
          throw new Error(data?.error || `HTTP ${response.status}`);
        }

        const data = (await response.json()) as { id: string; url: string };
        // Có frame → đổi hash sang presentation mode.
        const url = hasFrames
          ? data.url.replace("#share=", "#share-pres=")
          : data.url;
        const copied = await copyTextToClipboard(url);

        if (mode === "direct" && copied) {
          setIsOpen(false);
          setState({ kind: "idle" });
          notifyDrawLabShareStatus("copied");
          excalidrawAPI.setToast?.({
            message: t("drawlab.share.toastCopied", { days: 7 }),
            duration: 3800,
          });
          return;
        }

        setState({ kind: "done", url, copied });
        setIsOpen(true);
        if (!copied) {
          excalidrawAPI.setToast?.({
            message: t("drawlab.share.toastManual", { days: 7 }),
            duration: 3800,
          });
        }
        requestAnimationFrame(() => linkInputRef.current?.select());
      } catch (err) {
        const message =
          err instanceof TypeError
            ? t("drawlab.share.errorOffline")
            : err instanceof Error
            ? err.message
            : t("drawlab.share.errorUnknown");
        fail(message);
      }
    },
    [excalidrawAPI],
  );

  // Mở dialog hoặc chạy share trực tiếp qua window event từ toolbar/mobile.
  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ mode?: "direct" | "dialog" }>)
        .detail;
      if (detail?.mode === "dialog") {
        setState({ kind: "idle" });
        setIsOpen(true);
        return;
      }
      void handleCreateLink("direct");
    };
    window.addEventListener(DRAWLAB_OPEN_SHARE_EVENT, onOpen);
    return () => window.removeEventListener(DRAWLAB_OPEN_SHARE_EVENT, onOpen);
  }, [handleCreateLink]);

  const handleCopy = async () => {
    if (state.kind !== "done") {
      return;
    }
    try {
      const copied = await copyTextToClipboard(state.url);
      setState({ kind: "done", url: state.url, copied });
    } catch {
      linkInputRef.current?.select();
    }
  };

  if (!isOpen) {
    return null;
  }

  const isDark = excalidrawAPI?.getAppState().theme === "dark";

  return createPortal(
    <div
      className={`DrawLabShareDialog__overlay${
        isDark ? " DrawLabShareDialog__overlay--dark" : ""
      }`}
      onClick={close}
    >
      <div
        className={`DrawLabShareDialog${
          isDark ? " DrawLabShareDialog--dark" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={t("drawlab.share.title")}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="DrawLabShareDialog__header">
          <h2>{t("drawlab.share.title")}</h2>
          <button
            type="button"
            className="DrawLabShareDialog__close"
            onClick={close}
            aria-label={t("drawlab.share.close")}
          >
            ×
          </button>
        </div>

        <p className="DrawLabShareDialog__hint">
          {t("drawlab.share.expiresHint", { days: 7 })}
        </p>

        {state.kind === "idle" && (
          <button
            type="button"
            className="DrawLabShareDialog__primary"
            onClick={() => handleCreateLink("dialog")}
          >
            {t("drawlab.share.createLink")}
          </button>
        )}

        {state.kind === "uploading" && (
          <button
            type="button"
            className="DrawLabShareDialog__primary"
            disabled
          >
            {t("drawlab.share.creating")}
          </button>
        )}

        {state.kind === "error" && (
          <>
            <p className="DrawLabShareDialog__error">{state.message}</p>
            <button
              type="button"
              className="DrawLabShareDialog__primary"
              onClick={() => handleCreateLink("dialog")}
            >
              {t("drawlab.share.retry")}
            </button>
          </>
        )}

        {state.kind === "done" && (
          <div className="DrawLabShareDialog__result">
            <input
              ref={linkInputRef}
              className="DrawLabShareDialog__link"
              value={state.url}
              readOnly
              onFocus={(event) => event.currentTarget.select()}
            />
            <button
              type="button"
              className="DrawLabShareDialog__primary DrawLabShareDialog__copy"
              onClick={handleCopy}
            >
              {state.copied
                ? t("drawlab.share.copied")
                : t("drawlab.share.copyLink")}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
